import type { ComponentType, ReactNode } from 'react';
import { PageShell } from '~/components/page-shell';
import { Skeleton } from '~/components/ui/skeleton';
import { useCanAuthorCustomAgents, useIsAdmin } from '~/hooks/queries/use-is-admin';

/**
 * Route-level admin gate (FRONT-145). Renders its children only for
 * workspace admins; everyone else (and the brief window while the
 * `/api/auth/me` boot query is still resolving) sees a friendly notice
 * instead of a half-broken admin page.
 *
 * Wrap a whole route component with this — the guarded page keeps rendering
 * its own `PageShell`, which only mounts for admins, so there is no nested
 * shell. The backend routes these pages call are independently admin-gated;
 * this is the UI half that keeps non-admins from ever reaching a doomed call.
 *
 * Fails closed: `isLoading` is treated as "not admin yet" so there is no flash
 * of admin controls before the boot query lands.
 */
export function AdminOnly({
  children,
  title = 'Admin only',
}: {
  children: ReactNode;
  /** Title shown on the loading/denied chrome — match the page being guarded. */
  title?: string;
}) {
  const { isAdmin, isLoading } = useIsAdmin();

  if (isLoading) {
    return (
      <PageShell title={title}>
        <Skeleton className="h-48 w-full rounded-lg" />
      </PageShell>
    );
  }

  if (!isAdmin) {
    return (
      <PageShell title={title}>
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          You need workspace-admin access to view this page. Ask your workspace
          administrator if something here needs to change.
        </div>
      </PageShell>
    );
  }

  return <>{children}</>;
}

/**
 * Build a React Router `lazy` loader that wraps the imported route component in
 * `<AdminOnly>`. Works for both export styles in this app: a named `Component`
 * export (the common case) or a `default` export. Keeps the route table
 * declarative — `lazy: adminRoute(() => import('~/routes/x'), 'X')`.
 */
export function adminRoute(
  importer: () => Promise<{ Component?: ComponentType; default?: ComponentType }>,
  title: string,
) {
  return async () => {
    const m = await importer();
    const Inner = (m.Component ?? m.default) as ComponentType;
    return {
      Component: function AdminGuarded() {
        return (
          <AdminOnly title={title}>
            <Inner />
          </AdminOnly>
        );
      },
    };
  };
}

/**
 * Route-level BUILDER gate (FRONT-1519) — same shape as {@link AdminOnly},
 * for the custom-agent authoring pages. FRONT-2731: gates on
 * `canAuthorCustomAgents` (Builder, Admin, Super-admin — exactly what the
 * workspace service accepts), the same predicate the backend's custom-agent
 * write routes enforce; the wider builder ladder let a Pro User in and every
 * save then failed. Fails closed while the boot query loads so there is no
 * flash of authoring controls.
 */
export function BuilderOnly({
  children,
  title = 'Builders only',
}: {
  children: ReactNode;
  /** Title shown on the loading/denied chrome — match the page being guarded. */
  title?: string;
}) {
  const { canAuthorCustomAgents, isLoading } = useCanAuthorCustomAgents();

  if (isLoading) {
    return (
      <PageShell title={title}>
        <Skeleton className="h-48 w-full rounded-lg" />
      </PageShell>
    );
  }

  if (!canAuthorCustomAgents) {
    return (
      <PageShell title={title}>
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          Authoring custom agents needs the Builder or Admin role. Ask your workspace
          administrator to upgrade your role if this should be part of your
          work.
        </div>
      </PageShell>
    );
  }

  return <>{children}</>;
}

/** `builderRoute` mirrors {@link adminRoute} but gates on builder tier. */
export function builderRoute(
  importer: () => Promise<{ Component?: ComponentType; default?: ComponentType }>,
  title: string,
) {
  return async () => {
    const m = await importer();
    const Inner = (m.Component ?? m.default) as ComponentType;
    return {
      Component: function BuilderGuarded() {
        return (
          <BuilderOnly title={title}>
            <Inner />
          </BuilderOnly>
        );
      },
    };
  };
}
